import type { LucideIcon } from "lucide-react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Columns3,
  Copy,
  Download,
  Filter,
  MoreHorizontal,
  Pencil,
  Search,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { exportRowsCsv } from "../lib/csv-export";
import { ApiError } from "../lib/api";
import { showDeleteUndoToast, toast } from "../stores/toast";
import type {
  Column,
  DataTableInfiniteScroll,
  DataTableRowActions,
  DataTableSelectionConfig,
  DataTableToolbarConfig,
  TableFilter,
} from "./data-table-types";
export type {
  Column,
  ColumnFilterType,
  DataTableInfiniteScroll,
  DataTableRowActionItem,
  DataTableRowActions,
  DataTableSelectionConfig,
  DataTableToolbarConfig,
  FilterOperator,
  TableFilter,
} from "./data-table-types";
import {
  DataTableColumnsPanel,
  DataTableFiltersPanel,
  DataTableMoreMenu,
  rowMatchesFilter,
} from "./DataTablePanels";
import { DataTableCheckbox, DataTableRowMenu } from "./DataTableRowMenu";
import { useInfiniteScrollSentinel, useTableViewportHeight } from "../hooks/useTableViewport";
import { Button, EmptyState, PageLoader, PillSegmentSwitcher } from "./ui";

function compareSortValues(
  a: string | number | boolean | null | undefined,
  b: string | number | boolean | null | undefined,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function columnSearchText<T>(col: Column<T>, row: T): string {
  const raw = col.searchValue?.(row) ?? col.exportValue?.(row) ?? col.sortValue?.(row);
  return raw == null ? "" : String(raw);
}

function columnsToCsv<T>(columns: Column<T>[]): { header: string; value: (row: T) => string | number | boolean | null | undefined }[] {
  return columns.map((col) => ({
    header: col.header,
    value: (row: T) => (col.exportValue ?? col.sortValue)?.(row) ?? "",
  }));
}

function initialColumnOrder<T>(columns: Column<T>[]): string[] {
  return columns.map((c) => c.key);
}

function initialColumnVisible<T>(columns: Column<T>[]): Record<string, boolean> {
  return Object.fromEntries(columns.map((c) => [c.key, c.defaultVisible !== false]));
}

function deleteMessage(entityLabel: string, count: number): string {
  if (count === 1) return `${entityLabel} deleted`;
  return `${count} ${entityLabel.toLowerCase()}s deleted`;
}

export function DataTableToolbar({
  config,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onSearchSubmit,
  exportFilename,
  exportRows,
  exportColumns,
  activeFilterCount,
  onOpenFilters,
  onOpenColumns,
  onToggleMore,
  onCloseMore,
  moreOpen,
  moreRef,
  onRefresh,
  onClearFilters,
  hasFilters,
  bulkBar,
}: {
  config?: DataTableToolbarConfig;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  onSearchSubmit?: () => void;
  exportFilename?: string;
  exportRows?: unknown[];
  exportColumns?: Column<unknown>[];
  activeFilterCount?: number;
  onOpenFilters?: () => void;
  onOpenColumns?: () => void;
  onToggleMore?: () => void;
  onCloseMore?: () => void;
  moreOpen?: boolean;
  moreRef?: React.RefObject<HTMLDivElement | null>;
  onRefresh?: () => void;
  onClearFilters?: () => void;
  hasFilters?: boolean;
  bulkBar?: ReactNode;
}) {
  const canExport = Boolean(exportFilename && exportRows && exportColumns && exportRows.length > 0);
  const placeholder =
    config?.search?.placeholder ?? config?.searchPlaceholder ?? searchPlaceholder ?? "Search…";
  const showFilters = config?.showFilters !== false && Boolean(onOpenFilters);
  const showColumns = config?.showColumns !== false && Boolean(onOpenColumns);
  const hasViews = Boolean(config?.views && config.views.options.length > 0);

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-line bg-surface p-3">
        {bulkBar}
        <div
          className={`flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between ${bulkBar ? "mt-3 border-t border-line pt-3" : ""}`}
        >
          <div className="relative w-full max-w-md flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-2" />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearchSubmit?.();
              }}
              placeholder={placeholder}
              className="w-full rounded-xl border border-line bg-input-bg py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-2 outline-none transition-colors focus:border-brand-pink/60"
              aria-label="Search table"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            {hasViews && (
              <div className="hidden overflow-x-auto pb-0.5 md:block">
                <PillSegmentSwitcher
                  options={config!.views!.options}
                  value={config!.views!.value}
                  onChange={config!.views!.onChange}
                />
              </div>
            )}
            {showFilters && (
              <Button variant="outline" size="sm" onClick={onOpenFilters}>
                <Filter size={14} className="mr-1.5 inline" />
                Filters
                {activeFilterCount != null && activeFilterCount > 0 && (
                  <span className="ml-1.5 rounded-full bg-brand-pink/20 px-1.5 py-0.5 text-[10px] font-bold text-brand-pink">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            )}
            {showColumns && (
              <Button variant="outline" size="sm" onClick={onOpenColumns}>
                <Columns3 size={14} className="mr-1.5 inline" />
                Columns
              </Button>
            )}
            {canExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  exportRowsCsv(exportFilename!, columnsToCsv(exportColumns as Column<unknown>[]), exportRows as unknown[])
                }
              >
                <Download size={14} className="mr-1.5 inline" />
                Export
              </Button>
            )}
            {config?.trailing}
            {onToggleMore && (
              <div className="relative" ref={moreRef as React.RefObject<HTMLDivElement>}>
                <Button variant="ghost" size="sm" onClick={onToggleMore} aria-label="More actions">
                  <MoreHorizontal size={16} />
                </Button>
                <DataTableMoreMenu
                  open={Boolean(moreOpen)}
                  onClose={() => onCloseMore?.()}
                  anchorRef={moreRef ?? { current: null }}
                  onRefresh={onRefresh ?? config?.onRefresh}
                  onClearFilters={onClearFilters ?? (() => {})}
                  hasFilters={Boolean(hasFilters)}
                />
              </div>
            )}
          </div>
        </div>
        {hasViews && (
          <div className="mt-3 overflow-x-auto pb-0.5 md:hidden">
            <PillSegmentSwitcher
              options={config!.views!.options}
              value={config!.views!.value}
              onChange={config!.views!.onChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading,
  empty,
  activeKey,
  toolbar,
  exportFilename,
  searchPlaceholder,
  rowActions,
  selection,
  selectable,
  selectedKeys: controlledSelectedKeys,
  onSelectedKeysChange,
  rowMenuFirst = false,
  entityLabel = "Record",
  infiniteScroll,
  fillViewport = true,
  maxHeight,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: { icon?: LucideIcon; title: string; hint?: string; action?: ReactNode };
  activeKey?: string | null;
  toolbar?: DataTableToolbarConfig;
  exportFilename?: string;
  searchPlaceholder?: string;
  rowActions?: DataTableRowActions<T>;
  selection?: DataTableSelectionConfig<T>;
  /** When true, shows checkbox column and bulk actions (default: true if rowActions is set). */
  selectable?: boolean;
  /** Controlled row selection (use with onSelectedKeysChange). */
  selectedKeys?: Set<string>;
  onSelectedKeysChange?: (keys: Set<string>) => void;
  /** Render row action menu immediately after the selection column. */
  rowMenuFirst?: boolean;
  /** Singular label for delete toasts, e.g. "Product". */
  entityLabel?: string;
  /** Fetch next page when the user scrolls near the bottom of the table body. */
  infiniteScroll?: DataTableInfiniteScroll;
  /** When true (default), table body fills remaining viewport height. */
  fillViewport?: boolean;
  /** Fixed max body height in px — overrides viewport fill when set. */
  maxHeight?: number;
}) {
  const label = selection?.entityLabel ?? entityLabel;
  const isSelectable = selectable ?? Boolean(rowActions || selection);
  const showRowMenu = Boolean(
    rowActions?.onEdit || rowActions?.onDuplicate || rowActions?.onDelete || rowActions?.extra,
  );

  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);
  const [clientSearch, setClientSearch] = useState("");
  const [filters, setFilters] = useState<TableFilter[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => initialColumnOrder(columns));
  const [columnVisible, setColumnVisible] = useState<Record<string, boolean>>(() => initialColumnVisible(columns));
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Set<string>>(() => new Set());
  const isSelectionControlled = controlledSelectedKeys !== undefined;
  const selectedKeys = isSelectionControlled ? controlledSelectedKeys : internalSelectedKeys;
  const updateSelectedKeys = useCallback(
    (updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      if (isSelectionControlled) {
        const next =
          typeof updater === "function" ? updater(controlledSelectedKeys!) : updater;
        onSelectedKeysChange?.(next);
      } else {
        setInternalSelectedKeys(updater);
      }
    },
    [isSelectionControlled, controlledSelectedKeys, onSelectedKeysChange],
  );
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const tableAnchorRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const bodyMaxHeight = useTableViewportHeight(tableAnchorRef, fillViewport, maxHeight);

  useInfiniteScrollSentinel(
    scrollRef,
    sentinelRef,
    Boolean(infiniteScroll?.hasNextPage),
    Boolean(infiniteScroll?.hasNextPage),
    infiniteScroll?.isFetchingNextPage,
    infiniteScroll?.onLoadMore,
  );

  useEffect(() => {
    setColumnOrder((prev) => {
      const keys = columns.map((c) => c.key);
      const kept = prev.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !kept.includes(k));
      return [...kept, ...added];
    });
    setColumnVisible((prev) => {
      const next = { ...prev };
      for (const col of columns) {
        if (!(col.key in next)) next[col.key] = col.defaultVisible !== false;
      }
      return next;
    });
  }, [columns]);

  const isServerSearch = Boolean(toolbar?.search);
  const searchValue = toolbar?.search?.value ?? clientSearch;
  const onSearchChange = toolbar?.search?.onChange ?? setClientSearch;
  const onSearchSubmit = toolbar?.search?.onSubmit;

  const visibleColumns = useMemo(() => {
    const map = new Map(columns.map((c) => [c.key, c]));
    return columnOrder
      .map((key) => map.get(key))
      .filter((col): col is Column<T> => Boolean(col))
      .filter((col) => col.key === "actions" || columnVisible[col.key] !== false);
  }, [columns, columnOrder, columnVisible]);

  const sortedRows = useMemo(() => {
    if (!rows?.length || !sort) return rows;
    const col = visibleColumns.find((c) => c.key === sort.key);
    if (!col?.sortable || !col.sortValue) return rows;
    return [...rows].sort((a, b) => {
      const cmp = compareSortValues(col.sortValue!(a), col.sortValue!(b));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, visibleColumns]);

  const filteredRows = useMemo(() => {
    const base = sortedRows ?? rows;
    if (!base?.length || filters.length === 0) return base;
    return base.filter((row) =>
      filters.every((filter) => {
        const col = columns.find((c) => c.key === filter.columnKey);
        if (!col) return true;
        return rowMatchesFilter(row, col, filter);
      }),
    );
  }, [sortedRows, rows, filters, columns]);

  const displayRows = useMemo(() => {
    const base = filteredRows;
    if (!base?.length) return base;
    let result = base;
    if (!isServerSearch) {
      const q = clientSearch.trim().toLowerCase();
      if (q) {
        result = result.filter((row) =>
          visibleColumns.some((col) => columnSearchText(col, row).toLowerCase().includes(q)),
        );
      }
    }
    if (hiddenKeys.size > 0) {
      result = result.filter((row) => !hiddenKeys.has(rowKey(row)));
    }
    return result;
  }, [filteredRows, clientSearch, isServerSearch, visibleColumns, hiddenKeys, rowKey]);

  const visibleKeys = useMemo(() => (displayRows ?? []).map(rowKey), [displayRows, rowKey]);

  useEffect(() => {
    const visible = new Set(visibleKeys);
    updateSelectedKeys((prev) => {
      const next = new Set([...prev].filter((k) => visible.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleKeys, updateSelectedKeys]);

  const selectedCount = selectedKeys.size;
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys.has(k));
  const someVisibleSelected = visibleKeys.some((k) => selectedKeys.has(k)) && !allVisibleSelected;

  const selectedRows = useMemo(() => {
    if (!rows?.length || selectedKeys.size === 0) return [];
    return rows.filter((r) => selectedKeys.has(rowKey(r)));
  }, [rows, selectedKeys, rowKey]);

  const hasAdvancedFilters = filters.length > 0;
  const hasClientFilter = !isServerSearch && clientSearch.trim().length > 0;
  const hasAnyFilter = hasClientFilter || hasAdvancedFilters;
  const isFilteredEmpty = Boolean(rows?.length && displayRows && displayRows.length === 0 && hasAnyFilter);

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      updateSelectedKeys(new Set());
    } else {
      updateSelectedKeys(new Set(visibleKeys));
    }
  };

  const toggleSelectRow = (key: string) => {
    updateSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearSelection = () => updateSelectedKeys(new Set());

  const commitDeleteError = useCallback((err: unknown) => {
    toast("error", err instanceof ApiError ? err.message : "Delete failed");
  }, []);

  const requestDelete = useCallback(
    (targetRows: T[]) => {
      if (targetRows.length === 0) return;
      const keys = targetRows.map(rowKey);
      setHiddenKeys((prev) => new Set([...prev, ...keys]));
      updateSelectedKeys((prev) => {
        const next = new Set(prev);
        for (const k of keys) next.delete(k);
        return next;
      });

      showDeleteUndoToast(deleteMessage(label, targetRows.length), {
        onUndo: () => {
          setHiddenKeys((prev) => {
            const next = new Set(prev);
            for (const k of keys) next.delete(k);
            return next;
          });
        },
        onCommit: async () => {
          if (targetRows.length === 1 && rowActions?.onDelete) {
            await rowActions.onDelete(targetRows[0]!);
          } else if (selection?.onDelete) {
            await selection.onDelete(targetRows);
          } else if (rowActions?.onDelete) {
            for (const row of targetRows) {
              await rowActions.onDelete(row);
            }
          }
        },
        onCommitError: (err) => {
          commitDeleteError(err);
        },
      });
    },
    [rowKey, label, rowActions, selection, commitDeleteError, updateSelectedKeys],
  );

  const handleRowDelete = useCallback(
    (row: T) => {
      if (rowActions?.onDelete || selection?.onDelete) {
        requestDelete([row]);
      }
    },
    [rowActions, selection, requestDelete],
  );

  const handleBulkEdit = () => {
    const edit = selection?.onEdit ?? (rowActions?.onEdit ? (rows: T[]) => rowActions.onEdit!(rows[0]!) : undefined);
    if (!edit || selectedRows.length === 0) return;
    edit(selectedRows);
    clearSelection();
  };

  const handleBulkDuplicate = () => {
    const dup =
      selection?.onDuplicate ??
      (rowActions?.onDuplicate ? (rows: T[]) => rows.forEach((r) => rowActions.onDuplicate!(r)) : undefined);
    if (!dup || selectedRows.length === 0) return;
    dup(selectedRows);
    clearSelection();
    toast("success", selectedRows.length === 1 ? `${label} duplicated` : `${selectedRows.length} duplicated`);
  };

  const handleBulkDelete = () => {
    requestDelete(selectedRows);
  };

  function toggleSort(key: string) {
    const col = visibleColumns.find((c) => c.key === key);
    if (!col?.sortable) return;
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }

  function clearAllFilters() {
    setFilters([]);
    if (!isServerSearch) setClientSearch("");
    toolbar?.search?.onChange("");
  }

  const canBulkEdit = Boolean(selection?.onEdit || rowActions?.onEdit);
  const canBulkDuplicate = Boolean(selection?.onDuplicate || rowActions?.onDuplicate);
  const canBulkDelete = Boolean(selection?.onDelete || rowActions?.onDelete);

  const bulkBar =
    isSelectable && selectedCount > 0 ? (
      <div className="flex flex-wrap items-center gap-2 rounded-xl bg-brand-pink/10 px-1 py-1">
        <span className="text-sm font-medium text-foreground">
          {selectedCount} selected
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canBulkDelete && (
            <Button variant="danger" size="sm" onClick={handleBulkDelete}>
              <Trash2 size={14} className="mr-1.5 inline" />
              Delete
            </Button>
          )}
          {selection?.trailing}
          {canBulkEdit && (
            <Button variant="outline" size="sm" disabled={selectedCount !== 1} onClick={handleBulkEdit}>
              <Pencil size={14} className="mr-1.5 inline" />
              Edit
            </Button>
          )}
          {canBulkDuplicate && (
            <Button variant="outline" size="sm" onClick={handleBulkDuplicate}>
              <Copy size={14} className="mr-1.5 inline" />
              Duplicate
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      </div>
    ) : null;

  const rowMenuHeader = showRowMenu ? (
    <th className="w-10 px-0 py-2.5 align-middle" aria-label="Actions">
      <span className="sr-only">Actions</span>
    </th>
  ) : null;

  const renderRowMenuCell = (row: T, key: string) => {
    if (!showRowMenu) return null;
    const extraItems = rowActions?.extra?.(row) ?? [];
    const hasMenu =
      rowActions?.onEdit ||
      rowActions?.onDuplicate ||
      rowActions?.onDelete ||
      extraItems.some((item) => !item.hidden);
    if (!hasMenu) return null;

    return (
      <td className="w-10 px-0 py-2.5 align-middle" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center">
          <DataTableRowMenu
            row={row}
            open={openMenuKey === key}
            onToggle={() => setOpenMenuKey((prev) => (prev === key ? null : key))}
            onClose={() => setOpenMenuKey(null)}
            onEdit={rowActions?.onEdit}
            onDuplicate={
              rowActions?.onDuplicate
                ? (r) => {
                    rowActions.onDuplicate!(r);
                    toast("success", `${label} duplicated`);
                  }
                : undefined
            }
            onDelete={canBulkDelete ? handleRowDelete : undefined}
            extraItems={extraItems}
          />
        </div>
      </td>
    );
  };

  return (
    <div
      ref={tableAnchorRef}
      className={`flex min-h-0 flex-col gap-3 ${fillViewport ? "min-h-0 flex-1" : ""}`}
    >
      <div className="shrink-0">
      <DataTableToolbar
        config={toolbar}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder ?? toolbar?.searchPlaceholder}
        onSearchSubmit={onSearchSubmit}
        exportFilename={exportFilename}
        exportRows={displayRows ?? rows}
        exportColumns={visibleColumns as Column<unknown>[]}
        activeFilterCount={filters.length}
        onOpenFilters={() => setFiltersOpen(true)}
        onOpenColumns={() => setColumnsOpen(true)}
        onToggleMore={() => setMoreOpen((v) => !v)}
        onCloseMore={() => setMoreOpen(false)}
        moreOpen={moreOpen}
        moreRef={moreRef}
        onRefresh={toolbar?.onRefresh}
        onClearFilters={clearAllFilters}
        hasFilters={hasAnyFilter}
        bulkBar={bulkBar}
      />

      <DataTableFiltersPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        columns={columns}
        filters={filters}
        onChange={setFilters}
      />

      <DataTableColumnsPanel
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        columns={columns}
        columnOrder={columnOrder}
        columnVisible={columnVisible}
        onOrderChange={setColumnOrder}
        onVisibilityChange={setColumnVisible}
      />
      </div>

      {loading ? (
        <PageLoader />
      ) : !rows || rows.length === 0 ? (
        <EmptyState icon={empty?.icon} title={empty?.title ?? "Nothing here yet"} hint={empty?.hint} action={empty?.action} />
      ) : isFilteredEmpty ? (
        <EmptyState
          icon={empty?.icon}
          title="No matches"
          hint="Try adjusting filters."
          action={
            hasAnyFilter ? (
              <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div
          className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border border-line bg-surface ${fillViewport ? "flex-1" : ""}`}
          style={bodyMaxHeight != null ? { maxHeight: bodyMaxHeight } : undefined}
        >
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-max text-left text-sm">
            <thead className="sticky top-0 z-10 bg-surface">
              <tr className="border-b border-line text-[10px] uppercase tracking-wider text-muted-2">
                {isSelectable && (
                  <th className="w-10 px-0 py-2.5 align-middle">
                    <div className="flex items-center justify-center">
                      <DataTableCheckbox
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        onChange={toggleSelectAll}
                        ariaLabel="Select all rows"
                      />
                    </div>
                  </th>
                )}
                {rowMenuFirst && rowMenuHeader}
                {visibleColumns.map((col) => {
                  const active = sort?.key === col.key;
                  return (
                    <th
                      key={col.key}
                      className={`px-4 py-2.5 align-middle font-semibold ${col.hideSm ? "hidden md:table-cell" : ""} ${col.className ?? ""} ${
                        col.sortable ? "cursor-pointer select-none hover:text-muted" : ""
                      }`}
                      onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                      aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.header}
                        {col.sortable &&
                          (active ? (
                            sort!.dir === "asc" ? (
                              <ArrowUp size={12} className="text-brand-pink" />
                            ) : (
                              <ArrowDown size={12} className="text-brand-pink" />
                            )
                          ) : (
                            <ArrowUpDown size={12} className="opacity-40" />
                          ))}
                      </span>
                    </th>
                  );
                })}
                {!rowMenuFirst && rowMenuHeader}
              </tr>
            </thead>
            <tbody>
              {(displayRows ?? []).map((row) => {
                const key = rowKey(row);
                const isSelected = selectedKeys.has(key);
                return (
                  <tr
                    key={key}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={`border-b border-line/50 last:border-0 ${
                      onRowClick ? "cursor-pointer transition-colors hover:bg-hover" : ""
                    } ${activeKey === key || isSelected ? "bg-brand-pink/[0.07]" : ""}`}
                  >
                    {isSelectable && (
                      <td className="w-10 px-0 py-2.5 align-middle" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center">
                          <DataTableCheckbox
                            checked={isSelected}
                            onChange={() => toggleSelectRow(key)}
                            ariaLabel={`Select row ${key}`}
                          />
                        </div>
                      </td>
                    )}
                    {rowMenuFirst && renderRowMenuCell(row, key)}
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-2.5 align-middle ${col.hideSm ? "hidden md:table-cell" : ""} ${col.className ?? ""}`}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                    {!rowMenuFirst && renderRowMenuCell(row, key)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {infiniteScroll && (
            <div ref={sentinelRef} className="border-t border-line px-4 py-2.5 text-center text-[11px] text-muted">
              {infiniteScroll.isFetchingNextPage
                ? "Loading more…"
                : infiniteScroll.hasNextPage
                  ? ""
                  : (displayRows?.length ?? 0) > 0
                    ? "End of list"
                    : ""}
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

/** @deprecated Use DataTable infiniteScroll instead. */
export function Pagination({
  page,
  pageCount,
  onPage,
}: {
  page: number;
  pageCount: number;
  onPage: (p: number) => void;
}) {
  if (pageCount <= 1) return null;
  return (
    <div className="mt-3 flex items-center justify-end gap-2 text-xs text-white/60">
      <button
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className="rounded-lg bg-white/5 px-2.5 py-1.5 disabled:opacity-30"
      >
        ← Prev
      </button>
      <span>
        Page {page} / {pageCount}
      </span>
      <button
        disabled={page >= pageCount}
        onClick={() => onPage(page + 1)}
        className="rounded-lg bg-white/5 px-2.5 py-1.5 disabled:opacity-30"
      >
        Next →
      </button>
    </div>
  );
}
